/**
 * Sticky Cart Mobile Exclusivity
 * 
 * Implements mutual exclusivity between the floating sticky cart button 
 * and the in-page Add to Cart button on mobile devices (≤767px).
 * 
 * When the in-page Add to Cart button is visible in the viewport, 
 * the sticky cart is hidden. When the in-page button scrolls out of view,
 * the sticky cart becomes visible.
 */

class StickyCartMobileExclusivity {
  constructor() {
    this.stickyCart = null;
    this.inPageAddButton = null;
    this.observer = null;
    this.isMobile = false;
    this.initialized = false;
    this.hasScrolledPastButton = false; // Track if user has scrolled past the in-page button
    
    // Bind methods
    this.init = this.init.bind(this);
    this.checkViewport = this.checkViewport.bind(this);
    this.setupObserver = this.setupObserver.bind(this);
    this.handleIntersection = this.handleIntersection.bind(this);
    this.cleanup = this.cleanup.bind(this);
    
    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', this.init);
    } else {
      this.init();
    }
  }
  
  init() {
    // Find the sticky cart element
    this.stickyCart = document.querySelector('.xo-sticky-cart');
    
    // Find the in-page Add to Cart button (inside product info content)
    this.inPageAddButton = document.querySelector('.xo-product-info-content__add');
    
    if (!this.stickyCart || !this.inPageAddButton) {
      console.log('Sticky cart mobile exclusivity: Required elements not found');
      return;
    }
    
    // Ensure sticky cart is hidden on initial load (mobile only)
    if (window.innerWidth <= 767) {
      this.stickyCart.setAttribute('data-hide-on-mobile', 'true');
    }
    
    // Check viewport and setup
    this.checkViewport();
    
    // Listen for viewport changes
    window.addEventListener('resize', this.checkViewport);
    
    // Listen for orientation changes on mobile
    window.addEventListener('orientationchange', () => {
      setTimeout(this.checkViewport, 100);
    });
    
    this.initialized = true;
  }
  
  checkViewport() {
    const wasMobile = this.isMobile;
    this.isMobile = window.innerWidth <= 767;
    
    if (this.isMobile && !wasMobile) {
      // Switched to mobile - enable mutual exclusivity
      this.setupObserver();
    } else if (!this.isMobile && wasMobile) {
      // Switched to desktop - disable mutual exclusivity
      this.cleanup();
    } else if (this.isMobile && !this.observer) {
      // Already mobile but observer not setup
      this.setupObserver();
    }
  }
  
  setupObserver() {
    // Clean up existing observer
    if (this.observer) {
      this.observer.disconnect();
    }
    
    // Create intersection observer to watch the in-page button
    const options = {
      root: null, // Use viewport as root
      rootMargin: '-50px 0px -50px 0px', // Trigger slightly before button reaches edge
      threshold: [0, 0.1, 0.5, 0.9, 1.0] // Multiple thresholds for smooth detection
    };
    
    this.observer = new IntersectionObserver(this.handleIntersection, options);
    this.observer.observe(this.inPageAddButton);
  }
  
  handleIntersection(entries) {
    if (!this.isMobile) return;
    
    entries.forEach(entry => {
      const stickyInner = this.stickyCart.querySelector('.xo-sticky-cart__inner');
      
      // When in-page button is visible in viewport
      if (entry.isIntersecting && entry.intersectionRatio > 0) {
        // In-page button is visible - hide sticky cart and show in-page button
        this.stickyCart.setAttribute('data-hide-on-mobile', 'true');
        this.stickyCart.removeAttribute('data-show-on-mobile');
        
        if (stickyInner) {
          stickyInner.setAttribute('xo-hidden', '');
        }
      } else {
        // In-page button is out of view (user scrolled past it)
        this.hasScrolledPastButton = true;
        
        // Show sticky cart - in-page button is out of view
        this.stickyCart.removeAttribute('data-hide-on-mobile');
        this.stickyCart.setAttribute('data-show-on-mobile', 'true');
        
        // Remove xo-hidden to show the sticky cart
        if (stickyInner) {
          stickyInner.removeAttribute('xo-hidden');
        }
      }
    });
  }
  
  cleanup() {
    // Remove mutual exclusivity when switching to desktop
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    
    if (this.stickyCart) {
      this.stickyCart.removeAttribute('data-hide-on-mobile');
      this.stickyCart.removeAttribute('data-show-on-mobile');
      
      // Also remove xo-hidden from inner element
      const stickyInner = this.stickyCart.querySelector('.xo-sticky-cart__inner');
      if (stickyInner) {
        stickyInner.removeAttribute('xo-hidden');
      }
    }
    
    // Reset the scroll tracking flag
    this.hasScrolledPastButton = false;
  }
  
  destroy() {
    this.cleanup();
    window.removeEventListener('resize', this.checkViewport);
    window.removeEventListener('orientationchange', this.checkViewport);
    this.initialized = false;
  }
}

// Initialize the mutual exclusivity handler
if (typeof window !== 'undefined') {
  window.stickyCartMobileExclusivity = new StickyCartMobileExclusivity();
}
